import type {
  BillingStatus,
  OnboardingCallState,
  QualificationAnswers,
  QualificationDraft,
} from "@/billing/billingTypes";
import { goSolidApi } from "@/composables/useGoSolidApi";

/**
 * The corporate software subscription (backend: app/billing/api/billing_controller.py).
 *
 * Every call answers with the company's fresh `BillingStatus`, except `createCheckout`, which answers
 * with the Creem hosted-checkout URL to send the user to. Request bodies are the typed arguments below.
 *
 * Failures, all of which callers hand to `globalErrorHandler`:
 * - 409: the step no longer applies (already paid, not a billed company, the Platform Agreement not yet accepted, or
 *   a plan that is arranged through sales).
 * - 403 from `createCheckout` when an admin is acting for the account: only the account holder may subscribe.
 * - 429 from `createCheckout` after 10 checkouts per account in an hour.
 * - 503 from `createCheckout` when Creem cannot be reached or is not configured.
 */
export const billingApi = {
  getStatus: (companyId: number) =>
    goSolidApi.get<BillingStatus>(`/api/billing/companies/${companyId}/status`),
  /** Saves the answers given so far without choosing a plan, so a half-finished qualification is kept. */
  saveAnswers: (companyId: number, draft: QualificationDraft) =>
    goSolidApi.patch<BillingStatus>(
      `/api/billing/companies/${companyId}/qualification`,
      draft
    ),
  submitQualification: (companyId: number, answers: QualificationAnswers) =>
    goSolidApi.put<BillingStatus>(
      `/api/billing/companies/${companyId}/qualification`,
      answers
    ),
  createCheckout: (companyId: number) =>
    goSolidApi.post<{ checkout_url: string }>(
      `/api/billing/companies/${companyId}/checkouts`
    ),
  recordOnboardingCall: (companyId: number, state: OnboardingCallState) =>
    goSolidApi.put<BillingStatus>(
      `/api/billing/companies/${companyId}/onboarding-call`,
      { state }
    ),
  requestDemo: (companyId: number) =>
    goSolidApi.post<BillingStatus>(
      `/api/billing/companies/${companyId}/demo-requests`
    ),
};
